import {useCallback, useEffect, useLayoutEffect, useRef, useState} from "react";
import {cryptoRandomIndex} from "./cryptoRandom";

/**
 * The reel renders a fixed-length strip regardless of pool size: a 20,000-ticket
 * pool costs exactly the same DOM as a 5-ticket one. The winner is chosen up
 * front from the *full* pool and then written into the strip at the landing slot,
 * so sampling for display never affects who wins.
 */
export const STRIP_LENGTH = 60;
export const VISIBLE_CELLS = 3;
// Lands in the middle cell of the final window: after translating by
// (STRIP_LENGTH - VISIBLE_CELLS) rows the window shows the last 3 cells.
export const WINNER_SLOT = STRIP_LENGTH - 2;

export const buildStrip = (pool, winner) => {
    const strip = new Array(STRIP_LENGTH);
    // Walk from a random offset so consecutive spins don't scroll identical text.
    const offset = cryptoRandomIndex(pool.length);
    for (let i = 0; i < STRIP_LENGTH; i++) {
        strip[i] = pool[(offset + i) % pool.length];
    }
    strip[WINNER_SLOT] = winner;
    return strip;
};

const buildIdleStrip = (pool) => pool.slice(0, STRIP_LENGTH);

const prefersReducedMotion = () =>
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * @param pool the tickets that may be drawn — the only source a winner comes from
 * @param idlePool tickets to *display* when nothing is drawable, e.g. once the
 *        draw is complete. Never drawn from; purely so the reel does not sit
 *        there as an empty frame at the end of an event.
 */
export const useSlotReel = ({pool, idlePool = pool, spinDuration, meritWinner = null, onSettled}) => {
    const trackRef = useRef(null);
    const measureRef = useRef(null);
    const teardownRef = useRef(null);

    const [cellHeight, setCellHeight] = useState(56);
    const [strip, setStrip] = useState([]);
    const [spinning, setSpinning] = useState(false);
    const [winnerValue, setWinnerValue] = useState(null);

    // Latest callback without giving `spin` a new identity on every parent render.
    const onSettledRef = useRef(onSettled);
    useEffect(() => {
        onSettledRef.current = onSettled;
    });

    // Keep the sub-pixel value. The cell height is fluid (clamp/vh), so rounding
    // would compound over the ~57 rows of travel and land the reel visibly off
    // the winner — half a cell out at worst.
    //
    // Returns the height as well as storing it, so `spin` can use the value it
    // just measured instead of waiting a render for the state to catch up.
    const measureCell = useCallback(() => {
        const height = measureRef.current?.getBoundingClientRect().height;
        if (height && height > 0) {
            setCellHeight(height);
            return height;
        }
        return null;
    }, []);

    // Wrapped, not passed directly: measureCell returns the height now, and React
    // treats an effect's return value as its cleanup function — a number there
    // throws "destroy is not a function" when the screen unmounts.
    useLayoutEffect(() => {
        measureCell();
    }, [measureCell]);

    // vh-based sizing changes with the window; re-measure so the landing stays true.
    useEffect(() => {
        // Measured synchronously. This used to defer to requestAnimationFrame,
        // which never runs while the tab is not painting — so a window resized in
        // a background tab left the stored height stale, and the next spin
        // multiplied that stale value by 57 rows of travel. One
        // getBoundingClientRect per resize event is cheap; the browser already
        // rate-limits the event itself.
        window.addEventListener("resize", measureCell);
        return () => window.removeEventListener("resize", measureCell);
    }, [measureCell]);

    const teardown = useCallback(() => {
        teardownRef.current?.();
        teardownRef.current = null;
    }, []);

    // Reset to a resting strip whenever the pool changes (i.e. after a confirmed winner).
    useEffect(() => {
        teardown();
        const track = trackRef.current;
        if (track) {
            track.style.transition = "none";
            track.style.transform = "translateY(0px)";
        }
        setSpinning(false);
        setWinnerValue(null);
        // Display and eligibility are deliberately separate. The draw ends with an
        // empty `pool` — every prize awarded — and rendering that literally left
        // the reel a blank frame on stage. `spin` still refuses an empty `pool`
        // below, so showing these tickets can never put one in play.
        setStrip(buildIdleStrip(pool.length > 0 ? pool : idlePool));
    }, [pool, idlePool, teardown]);

    useEffect(() => teardown, [teardown]);

    const spin = useCallback(() => {
        const track = trackRef.current;
        if (!track || spinning || pool.length === 0) return;

        // `meritWinner` is the caller's decision that this spin owes a specific
        // ticket; everything else is chance. The membership check keeps a stale
        // merit ticket that no longer belongs to this pool from landing anyway —
        // better a spin by chance than a winner the draw already ruled out.
        const winner = meritWinner && pool.includes(meritWinner)
            ? meritWinner
            : pool[cryptoRandomIndex(pool.length)];

        // Measure now rather than trusting the stored height. The travel is 57
        // cells, so the height is multiplied 57 times and any drift between the
        // state and the DOM lands the reel whole rows away from the winner — the
        // audience reads the wrong ticket off the reel while the modal names the
        // right one. Anything that changes the cell's computed height without a
        // repaint (a resize, a zoom, moving the window to the projector) opens
        // that gap; measuring at spin time closes it for all of them.
        const liveCellHeight = measureCell() ?? cellHeight;
        const distance = (STRIP_LENGTH - VISIBLE_CELLS) * liveCellHeight;
        // The stylesheet disables the reel transition under reduced motion, so the
        // transform lands instantly — announce on a short beat instead of stalling
        // for the full spin duration.
        const effectiveDuration = prefersReducedMotion() ? 0.4 : spinDuration;

        setWinnerValue(null);
        setSpinning(true);
        setStrip(buildStrip(pool, winner));

        track.style.transition = "none";
        track.style.transform = "translateY(0px)";

        let settled = false;
        const settle = () => {
            if (settled) return;
            settled = true;
            teardown();
            setSpinning(false);
            setWinnerValue(winner);
            onSettledRef.current?.(winner);
        };

        // transitionend never fires under prefers-reduced-motion (the CSS disables
        // the transition) or in a tab that is not painting. Without this fallback
        // the reel hangs forever mid-draw.
        //
        // Armed here rather than inside the frames below, and this is the whole
        // point: a hidden tab never runs requestAnimationFrame, so a fallback
        // armed in there would never be scheduled at all — leaving the case it
        // exists for the one case it could not cover.
        const fallback = setTimeout(settle, effectiveDuration * 1000 + 500);

        let outerFrame = 0;
        let innerFrame = 0;
        teardownRef.current = () => {
            clearTimeout(fallback);
            cancelAnimationFrame(outerFrame);
            cancelAnimationFrame(innerFrame);
            track.removeEventListener("transitionend", settle);
        };

        // Two frames: the browser must paint the new strip at offset 0 before the
        // transition is applied, or it animates from the wrong starting position.
        outerFrame = requestAnimationFrame(() => {
            innerFrame = requestAnimationFrame(() => {
                track.style.transition = `transform ${effectiveDuration}s cubic-bezier(0.12, 0.01, 0, 1)`;
                track.style.transform = `translateY(-${distance}px)`;
                track.addEventListener("transitionend", settle, {once: true});
            });
        });
    }, [pool, spinning, cellHeight, spinDuration, meritWinner, measureCell, teardown]);

    return {trackRef, measureRef, cellHeight, strip, spinning, winnerValue, spin};
};
